import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { MonthYearSelect } from "@lending/components/MonthYearSelect/MonthYearSelect";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { Select } from "@lending/components/Select/Select";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useCreateApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useCreateApplicantIncomeDetailsQuery";
import { useListApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useListApplicantIncomeDetailsQuery";
import { usePutApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/usePutApplicantIncomeDetailsQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { EMPLOYMENT_TYPES, IncomeTypeEnum } from "@lending/types";
import { findPrimaryApplicant } from "@lending/utils/applicantHelpers";
import {
  createEmploymentDetailsSchema,
  formatEmploymentStartDate,
  getEmploymentFieldResetPayload,
} from "../employmentDetailsForm";
import styles from "./AddIncome.module.css";
import { useStore } from "../../../contexts/StoreContext";

interface FormData {
  income_type: IncomeTypeEnum | undefined;
  job_title: string;
  employer_name: string;
  employment_start_month: string;
  employment_start_year: string;
}

function useDisplayedEmploymentDetailsIncomeType(selectedIncomeType: IncomeTypeEnum | undefined) {
  const hasSelectedIncomeType = Boolean(selectedIncomeType);
  const isRetiredSelected = selectedIncomeType === "retirement";
  const [displayedIncomeType, setDisplayedIncomeType] = useState<IncomeTypeEnum | undefined>();

  useEffect(() => {
    if (selectedIncomeType && selectedIncomeType !== "retirement") {
      setDisplayedIncomeType(selectedIncomeType);
    }
  }, [selectedIncomeType]);

  const displayedDetailsIncomeType =
    !isRetiredSelected && hasSelectedIncomeType ? selectedIncomeType : displayedIncomeType;

  const handleDetailsTransitionEnd = useCallback(() => {
    if (!hasSelectedIncomeType || isRetiredSelected) {
      setDisplayedIncomeType(undefined);
    }
  }, [hasSelectedIncomeType, isRetiredSelected]);

  // Keep the last non-retirement selection mounted while the panel collapses so
  // the exit animation can finish before the details content is removed.
  return {
    displayedDetailsIncomeType,
    handleDetailsTransitionEnd,
    hasSelectedIncomeType,
    isDisplayingOther: displayedDetailsIncomeType === "other",
    isRetiredSelected,
  };
}

function AddIncome() {
  const { l10n } = useLocalization();
  const { showGenericErrorToast } = useToastsContext();
  const { goNext, startOver, goBack } = useFlowNavigation();
  const { updateData } = useStore();

  const { data: applicantResponse } = useListApplicationApplicantDetailsQuery();
  const applicant = findPrimaryApplicant(applicantResponse?.results);

  const { data: incomeDetailsResponse } = useListApplicantIncomeDetailsQuery();
  const incomeDetails =
    incomeDetailsResponse?.results.find(
      (income) =>
        income.is_primary && income.lending_application_applicant_details === applicant?.id
    ) ?? null;
  const lastInitializedFormKey = useRef<string | null>(null);
  const schema = createEmploymentDetailsSchema(l10n);

  const { handleSubmit, control, reset, watch } = useForm<FormData>({
    defaultValues: {
      income_type: undefined,
      job_title: "",
      employer_name: "",
      employment_start_month: "",
      employment_start_year: "",
    },
    resolver: zodResolver(schema),
  });

  const selectedIncomeType = watch("income_type") as IncomeTypeEnum | undefined;
  const {
    displayedDetailsIncomeType,
    handleDetailsTransitionEnd,
    hasSelectedIncomeType,
    isDisplayingOther,
    isRetiredSelected,
  } = useDisplayedEmploymentDetailsIncomeType(selectedIncomeType);

  useEffect(() => {
    if (!incomeDetails) {
      return;
    }

    const initializationKey = [
      incomeDetails.id,
      incomeDetails.income_type ?? "",
      incomeDetails.job_title ?? "",
      incomeDetails.employer_name ?? "",
      incomeDetails.employment_start_month ?? "",
      incomeDetails.employment_start_year ?? "",
    ].join("|");

    if (lastInitializedFormKey.current === initializationKey) {
      return;
    }

    lastInitializedFormKey.current = initializationKey;

    reset({
      income_type: incomeDetails.income_type as IncomeTypeEnum,
      job_title: incomeDetails.job_title ?? "",
      employer_name: incomeDetails.employer_name ?? "",
      ...formatEmploymentStartDate(
        incomeDetails.employment_start_month,
        incomeDetails.employment_start_year
      ),
    });
    updateData({
      incomeType: incomeDetails.income_type,
      incomeDetailsId: incomeDetails.id,
    });
  }, [incomeDetails, reset, updateData]);

  // Both saves are awaited (we read the created record and then navigate), so onSubmit
  // owns the failure in its catch rather than splitting it into an onError callback.
  const { mutateAsync: updateApplicantIncome, isPending: isUpdating } =
    usePutApplicantIncomeDetailsQuery();

  const { mutateAsync: createApplicantIncome, isPending: isCreating } =
    useCreateApplicantIncomeDetailsQuery();

  async function onSubmit(data: FormData) {
    const nextIncomeType = data.income_type!;

    if (!applicant) {
      showGenericErrorToast();
      startOver();
      return;
    }

    const body = {
      income_type: nextIncomeType,
      amount: incomeDetails?.amount ?? 0,
      job_title: nextIncomeType === "retirement" ? null : data.job_title,
      employer_name:
        nextIncomeType === "retirement" || nextIncomeType === "other" ? null : data.employer_name,
      employment_start_month:
        nextIncomeType === "retirement" || nextIncomeType === "other"
          ? null
          : Number(data.employment_start_month),
      employment_start_year:
        nextIncomeType === "retirement" || nextIncomeType === "other"
          ? null
          : Number(data.employment_start_year),
      ...getEmploymentFieldResetPayload(nextIncomeType),
    };

    try {
      if (incomeDetails?.id) {
        await updateApplicantIncome({
          params: { path: { uuid: incomeDetails.id } },
          // @ts-expect-error OpenAPI marks these fields as required on update.
          body,
        });
        updateData({ incomeType: nextIncomeType, incomeDetailsId: incomeDetails.id });
      } else {
        const createdIncome = await createApplicantIncome({
          // @ts-expect-error OpenAPI marks `id` as required on create.
          body: {
            lending_application_applicant_details: applicant.id,
            ...body,
          },
        });
        updateData({ incomeType: nextIncomeType, incomeDetailsId: createdIncome?.id });
      }

      goNext({ incomeType: nextIncomeType });
    } catch (error) {
      // Stay on the page; the user can retry.
      console.error("Error saving applicant income:", error);
      showGenericErrorToast();
    }
  }

  return (
    <PageLayout>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "employment-details-heading",
            null,
            "Tell us about your employment"
          )}
          subheading=""
        />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.employmentForm}>
            <div className={styles.selectWrapper}>
              <Select
                name="income_type"
                label={l10n.getString("add-income-employment-type", null, "Employment type")}
                control={control}
                items={EMPLOYMENT_TYPES}
              />
            </div>

            <div
              className={`${styles.employmentDetailsWrapper} ${
                hasSelectedIncomeType && !isRetiredSelected ? styles.visible : styles.hidden
              }`}
              aria-hidden={!hasSelectedIncomeType || isRetiredSelected}
              onTransitionEnd={handleDetailsTransitionEnd}
            >
              <div className={styles.employmentDetailsInner}>
                {displayedDetailsIncomeType && (
                  <div className={styles.employmentDetails}>
                    <TextInput
                      name="job_title"
                      label={
                        isDisplayingOther
                          ? l10n.getString(
                              "employment-details-job-description",
                              null,
                              "Description"
                            )
                          : l10n.getString("employment-details-job-title", null, "Job title")
                      }
                      control={control}
                    />

                    {!isDisplayingOther && (
                      <>
                        <TextInput
                          name="employer_name"
                          label={l10n.getString("employment-details-company", null, "Company")}
                          control={control}
                        />
                        <MonthYearSelect
                          control={control}
                          monthName="employment_start_month"
                          yearName="employment_start_year"
                          maxDate={{
                            month: new Date().getMonth() + 1,
                            year: new Date().getFullYear(),
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={goBack} />
              <Button
                label={l10n.getString("add-income-submit", null, "Next")}
                type="submit"
                isLoading={isUpdating || isCreating}
              />
            </Row>
          </div>
        </form>
      </PageContent>
    </PageLayout>
  );
}

export { AddIncome };
